import type { Practice } from "@brandex/api-client";

import { WEEKDAYS, chamberLocation, emptyDraft, validateStep, type OnboardingDraft, type StepErrors } from "../onboarding/draft";
import { formatLocation } from "../onboarding/location";

/**
 * The profile page edits the same fields as onboarding, so it reuses the
 * onboarding draft, steps and validation (one set of rules for both).
 */
export function practiceToDraft(p: Practice): OnboardingDraft {
  const { profile } = p;
  return {
    ...emptyDraft(),
    fullName: profile.full_name,
    specialty: profile.specialty,
    designation: profile.designation,
    registrationNo: profile.registration_no ?? "",
    languages: profile.languages,
    degrees: profile.degrees.map((g) => ({ title: g.title, institution: g.institution ?? "", year: g.year === null || g.year === undefined ? "" : String(g.year) })),
    bio: profile.bio,
    chambers: p.chambers.map((c) => ({
      id: c.id,
      name: c.name,
      address: c.address,
      days: c.days,
      opens: c.opens,
      closes: c.closes,
      slotMinutes: c.slot_minutes,
      location: formatLocation(c.location),
    })),
  };
}

/** Errors for the details section (practice + qualifications fields). */
export function validateDetails(d: OnboardingDraft): StepErrors {
  return { ...validateStep("practice", d), ...validateStep("qualifications", d) };
}

export function toProfileRequest(d: OnboardingDraft) {
  return {
    full_name: d.fullName.trim(),
    specialty: d.specialty ?? ("other" as const),
    designation: d.designation.trim(),
    registration_no: d.registrationNo.trim() === "" ? null : d.registrationNo.trim(),
    languages: d.languages,
    degrees: d.degrees.map((g) => ({
      title: g.title.trim(),
      institution: g.institution.trim(),
      year: /^\d{4}$/.test(g.year.trim()) ? Number(g.year.trim()) : null,
    })),
    bio: d.bio.trim(),
  };
}

export function toChambersRequest(d: OnboardingDraft) {
  return {
    chambers: d.chambers.map((c) => ({
      ...(c.id === undefined ? {} : { id: c.id }),
      name: c.name.trim(),
      address: c.address.trim(),
      days: WEEKDAYS.filter((w) => c.days.includes(w)),
      opens: c.opens,
      closes: c.closes,
      slot_minutes: c.slotMinutes,
      location: chamberLocation(c),
    })),
  };
}
